import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Switch,
  TextField
} from '@mui/material';
import {
  Print as PrintIcon,
  Description as A4Icon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface PrintSettingsProps {
  onSave?: (settings: PrintSettingsData) => void;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

export interface PrintSettingsData {
  defaultPrintType: 'a4' | 'thermal';
  enablePrintOptions: boolean;
  autoOpenPrintDialog: boolean;
  thermalPrinterWidth: number;
  a4PageSize: 'A4' | 'Letter';
  printQuality: 'draft' | 'normal' | 'high';
  enablePrintPreview: boolean;
  savePrintHistory: boolean;
  maxPrintHistory: number;
}

const PrintSettings: React.FC<PrintSettingsProps> = ({
  onSave,
  onError,
  onSuccess
}) => {
  const [settings, setSettings] = useState<PrintSettingsData>({
    defaultPrintType: 'a4',
    enablePrintOptions: true,
    autoOpenPrintDialog: false,
    thermalPrinterWidth: 300,
    a4PageSize: 'A4',
    printQuality: 'normal',
    enablePrintPreview: true,
    savePrintHistory: true,
    maxPrintHistory: 100
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // تحميل الإعدادات من localStorage
      const savedSettings = localStorage.getItem('print_settings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsedSettings }));
      }

      // تحميل الإعدادات من Firebase إذا كانت متاحة
      try {
        const { getSystemSettings } = await import('../services/systemSettingsService');
        const systemSettings = await getSystemSettings();
        
        if (systemSettings?.printSettings) {
          setSettings(prev => ({ ...prev, ...systemSettings.printSettings }));
        }
      } catch (error) {
        console.log('لم يتم العثور على إعدادات الطباعة في Firebase، استخدام الإعدادات المحلية');
      }

    } catch (error) {
      console.error('خطأ في تحميل إعدادات الطباعة:', error);
      onError?.('فشل في تحميل إعدادات الطباعة');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // حفظ في localStorage
      localStorage.setItem('print_settings', JSON.stringify(settings));

      // حفظ في Firebase إذا كانت متاحة
      try {
        const { getSystemSettings, updateSystemSettings } = await import('../services/systemSettingsService');
        const systemSettings = await getSystemSettings();
        
        const updatedSettings = {
          ...systemSettings,
          printSettings: settings,
          lastUpdated: new Date()
        };

        await updateSystemSettings(updatedSettings);
      } catch (error) {
        console.log('لم يتم حفظ إعدادات الطباعة في Firebase، تم الحفظ محلياً فقط');
      }

      onSuccess?.('تم حفظ إعدادات الطباعة بنجاح');
    } catch (error) {
      console.error('خطأ في حفظ إعدادات الطباعة:', error);
      onError?.('فشل في حفظ إعدادات الطباعة');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      defaultPrintType: 'a4',
      enablePrintOptions: true,
      autoOpenPrintDialog: false,
      thermalPrinterWidth: 300,
      a4PageSize: 'A4',
      printQuality: 'normal',
      enablePrintPreview: true,
      savePrintHistory: true,
      maxPrintHistory: 100
    });
  };

  const handleSettingChange = (key: keyof PrintSettingsData, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>جاري تحميل إعدادات الطباعة...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <PrintIcon />
        إعدادات الطباعة
      </Typography>

      <Grid container spacing={3}>
        {/* إعدادات نوع الطباعة الافتراضي */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                نوع الطباعة الافتراضي
              </Typography>
              
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">اختر نوع الطباعة الافتراضي</FormLabel>
                <RadioGroup
                  value={settings.defaultPrintType}
                  onChange={(e) => handleSettingChange('defaultPrintType', e.target.value)}
                  sx={{ mt: 1 }}
                >
                  <FormControlLabel
                    value="a4"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <A4Icon color="primary" />
                        <Box>
                          <Typography variant="body1">طباعة A4</Typography>
                          <Typography variant="caption" color="text.secondary">
                            مناسب للطابعات العادية
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="thermal"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon color="primary" />
                        <Box>
                          <Typography variant="body1">طابعة حرارية</Typography>
                          <Typography variant="caption" color="text.secondary">
                            مناسب للطابعات الحرارية الصغيرة
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* إعدادات عامة */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                الإعدادات العامة
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enablePrintOptions}
                      onChange={(e) => handleSettingChange('enablePrintOptions', e.target.checked)}
                    />
                  }
                  label="تمكين خيارات الطباعة المتقدمة"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoOpenPrintDialog}
                      onChange={(e) => handleSettingChange('autoOpenPrintDialog', e.target.checked)}
                    />
                  }
                  label="فتح حوار خيارات الطباعة تلقائياً"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enablePrintPreview}
                      onChange={(e) => handleSettingChange('enablePrintPreview', e.target.checked)}
                    />
                  }
                  label="تمكين معاينة الطباعة"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.savePrintHistory}
                      onChange={(e) => handleSettingChange('savePrintHistory', e.target.checked)}
                    />
                  }
                  label="حفظ تاريخ الطباعة"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* إعدادات الطابعة الحرارية */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon />
                إعدادات الطابعة الحرارية
              </Typography>
              
              <TextField
                fullWidth
                label="عرض الطابعة الحرارية (بالبكسل)"
                type="number"
                value={settings.thermalPrinterWidth}
                onChange={(e) => handleSettingChange('thermalPrinterWidth', parseInt(e.target.value) || 300)}
                helperText="العرض الموصى به: 300-400 بكسل"
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth>
                <FormLabel>جودة الطباعة</FormLabel>
                <RadioGroup
                  value={settings.printQuality}
                  onChange={(e) => handleSettingChange('printQuality', e.target.value)}
                  row
                >
                  <FormControlLabel value="draft" control={<Radio />} label="مسودة" />
                  <FormControlLabel value="normal" control={<Radio />} label="عادية" />
                  <FormControlLabel value="high" control={<Radio />} label="عالية" />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* إعدادات A4 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <A4Icon />
                إعدادات A4
              </Typography>
              
              <FormControl fullWidth>
                <FormLabel>حجم الصفحة</FormLabel>
                <RadioGroup
                  value={settings.a4PageSize}
                  onChange={(e) => handleSettingChange('a4PageSize', e.target.value)}
                  row
                >
                  <FormControlLabel value="A4" control={<Radio />} label="A4" />
                  <FormControlLabel value="Letter" control={<Radio />} label="Letter" />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* إعدادات تاريخ الطباعة */}
        {settings.savePrintHistory && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  إعدادات تاريخ الطباعة
                </Typography>
                
                <TextField
                  fullWidth
                  label="الحد الأقصى لعدد سجلات الطباعة المحفوظة"
                  type="number"
                  value={settings.maxPrintHistory}
                  onChange={(e) => handleSettingChange('maxPrintHistory', parseInt(e.target.value) || 100)}
                  helperText="عند الوصول لهذا العدد، سيتم حذف أقدم السجلات"
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* أزرار التحكم */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={saving}
        >
          إعادة تعيين
        </Button>
        
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>ملاحظة:</strong> سيتم تطبيق هذه الإعدادات على جميع عمليات الطباعة في النظام
        </Typography>
      </Alert>
    </Box>
  );
};

export default PrintSettings;

